// ADVENTURE — rail panel. It lists the places the cat can travel to (config/destinations.js): Capital City, Bamboo Badlands, and two
// more that are greyed out until they are built. Click → travel screen and load → the cat spawns there → RETURN HOME
// (see scene/capitalScene.js and scene/landScene.js).
//   (The older timed-adventure "Active" view below is dormant: nothing starts those adventures from here any more.)

import { Events, on, emit } from './eventBus.js'
import { TripConfig, DealConfig } from '../config/capital.js'
import { Destinations } from '../config/destinations.js'
import {
  recall as advRecall, abandon as advAbandon, setMode as advSetMode,
  currentAdventure, currentDef, isAdventuring,
} from '../game/adventureManager.js'
import { formatCoins } from './format.js'
import { makeToggleable } from './railPanel.js'

const SCRIBE_KIND = {
  pigeon:  { icon: '🐦', label: 'Pigeon'  },
  smoke:   { icon: '💨', label: 'Smoke'   },
  courier: { icon: '🏇', label: 'Courier' },
}

// In-panel log of scribe messages from the current adventure. Cleared on end.
let scribeLog = []
// Discoveries flash in the active panel — rebuilt from profile.adventure each render.

const HINT_SEEN_KEY = 'mao_adv_hint_seen'

export function mountAdventurePanel({ root, toggleBtn }) {
  root.classList.add('hud__build')
  makeToggleable(root, toggleBtn)
  root.innerHTML = `<div data-adventure-view></div>`
  const view = root.querySelector('[data-adventure-view]')

  // First-time hint: pulse the ADVENTURE rail button (with a gold "new"
  // dot) until the player opens the panel once. Persisted in localStorage so
  // it never nags again on this device.
  installHint(toggleBtn)

  const refresh = () => render(view)
  refresh()

  // Bus subscriptions — re-render on each meaningful transition.
  on(Events.AdventureStart,      () => { scribeLog = []; refresh() })
  on(Events.AdventureEnd,        () => { refresh() })
  on(Events.AdventureModeChange, () => { refresh() })
  on(Events.AdventureDiscovery,  () => { refresh() })
  on(Events.AdventureScribeBeat, ({ kind, message }) => {
    scribeLog = [{ kind, message, at: Date.now() }, ...scribeLog].slice(0, 8)
    refresh()
  })
  on(Events.AdventureTick,       () => updateCountdown(view))

  // Bound actions via delegation. Keeps re-rendered DOM live.
  root.addEventListener('click', (e) => {
    // Backdrop click (the dim scrim, outside the card) closes the modal.
    if (e.target === root) {
      root.classList.remove('open')
      return
    }
    const a = e.target.closest('[data-adv-action]')
    if (!a) return
    const action = a.dataset.advAction
    if (action === 'visit-capital') {
      // Capital City is the first destination: the same travel screen, load and trip as ever — just started from here.
      root.classList.remove('open')
      emit(Events.VisitCapital)
      return
    }
    if (action === 'visit-land') {
      // The other lands work exactly the same way (scene/landScene.js).
      root.classList.remove('open')
      emit(Events.VisitLand, { id: a.dataset.id })
      return
    }
    if (action === 'set-mode') {
      advSetMode(a.dataset.mode)
      return
    }
    if (action === 'recall') {
      if (confirm('Recall the cat early? You\'ll get 70% of discovered coins.')) {
        advRecall()
      }
      return
    }
    if (action === 'abandon') {
      const def = currentDef()
      const fine = formatCoins(def?.abandonFine ?? 0)
      if (confirm(
        `Abandon the adventure and rush home?\n\n` +
        `The cat sprints back instantly — but you FORFEIT every coin and relic ` +
        `found this trip, AND pay a ${fine} scramble fine.\n\n` +
        `Use this only when home needs the cat NOW.`,
      )) {
        advAbandon()
        root.classList.remove('open') // drop straight back into the home scene
      }
      return
    }
    if (action === 'close') {
      root.classList.remove('open')
      return
    }
  })
}

function render(view) {
  if (isAdventuring()) renderActive(view)
  else renderPicker(view)
}

// Every card shares the same raid-risk line: leaving home for any place is the same trip (config/capital.js TripConfig).
function riskLine() {
  return `Home raid chance: none for the first ${Math.round(TripConfig.graceMs / 1000)}s, then ${Math.round(TripConfig.riskBasePerMin * 100)}%/min and rising`
}

// Capital City — fully built; its figures are the game's own.
function capitalCard(def) {
  return `<div class="adv__card adv__card--capital" data-biome="capital" style="--biome:${def.tint}">
      <div class="adv__card-head">
        <strong>${escape(def.name)} <span class="adv__cn">${escape(def.cn)}</span></strong>
        <span class="adv__card-time">no time limit</span>
      </div>
      <div class="adv__card-stat">Walk the great Samurai city: <strong>sell rice, collect an Honor stipend, find hidden pouches</strong></div>
      <div class="adv__card-stat dim">${riskLine()} · cutpurses and palace guards</div>
      <div class="adv__card-items">
        <span class="adv__chip">Rice contract</span>
        <span class="adv__chip">Honor stipend</span>
        <span class="adv__chip">Explorer's pouches (up to ${DealConfig.pouch.perTrip})</span>
        <span class="adv__chip">Hidden landmarks</span>
      </div>
      <button data-adv-action="visit-capital" class="adv__begin">VISIT CAPITAL</button>
    </div>`
}

// The other places: the same click → travel → spawn flow. A place that is not built yet is greyed out.
function landCard(def) {
  if (!def.built) {
    return `<div class="adv__card adv__card--land adv__card--soon" data-land="${def.id}" style="--biome:${def.tint}" aria-disabled="true">
      <div class="adv__card-head">
        <strong>${escape(def.name)}${def.cn ? ` <span class="adv__cn">${escape(def.cn)}</span>` : ''}</strong>
        <span class="adv__card-time">coming soon</span>
      </div>
      <button class="adv__begin" disabled>COMING SOON</button>
    </div>`
  }
  return `<div class="adv__card adv__card--land" data-land="${def.id}" style="--biome:${def.tint}">
      <div class="adv__card-head">
        <strong>${escape(def.name)}${def.cn ? ` <span class="adv__cn">${escape(def.cn)}</span>` : ''}</strong>
        <span class="adv__card-time">no time limit</span>
      </div>
      ${def.blurb ? `<div class="adv__card-stat">${escape(def.blurb)}</div>` : ''}
      <div class="adv__card-stat dim">${riskLine()}</div>
      ${def.chips ? `<div class="adv__card-items">${def.chips.map((c) => `<span class="adv__chip">${escape(c)}</span>`).join('')}</div>` : ''}
      <button data-adv-action="visit-land" data-id="${def.id}" class="adv__begin">TRAVEL</button>
    </div>`
}

function renderPicker(view) {
  const cards = Destinations.filter((d) => !d.hidden).map((d) => (d.id === 'capital' ? capitalCard(d) : landCard(d))).join('')
  view.innerHTML = `
    <button class="reg__close" data-adv-action="close" aria-label="Close">×</button>
    <div class="adv__scroll styled-scroll">
      <div class="adv__head">
        <div class="adv__title">ADVENTURES</div>
        <div class="adv__sub">Places to explore. While you're away, home is on its own — the Scribe keeps you informed.</div>
      </div>
      <div class="adv__cards">${cards}</div>
    </div>
  `
}

function renderActive(view) {
  const a = currentAdventure()
  const def = currentDef()
  if (!a || !def) return renderPicker(view)
  const mode = a.mode

  const discoveriesHtml = (a.discoveries ?? []).slice().reverse().map((d) => `
    <div class="adv__row">
      <span>✨ ${escape(d.item)}</span>
      <strong>+${formatCoins(d.amount)}</strong>
    </div>
  `).join('') || `<div class="adv__row dim">Nothing yet — switch to 🔎 DISCOVERY to start finding treasure.</div>`

  const scribeHtml = scribeLog.map((m) => `
    <div class="adv__scribe-row">
      <span class="adv__scribe-icon">${SCRIBE_KIND[m.kind]?.icon ?? '✉️'}</span>
      <span>${escape(m.message)}</span>
    </div>
  `).join('') || `<div class="adv__row dim">No messages from home yet.</div>`

  view.innerHTML = `
    <button class="reg__close" data-adv-action="close" aria-label="Close">×</button>
    <div class="adv__scroll styled-scroll">
      <div class="adv__head" style="--biome:${def.backdrop}">
        <div class="adv__title">${escape(def.name)}</div>
        <div class="adv__sub">Your cat is in the ${escape(def.biome)}. Stay or recall any time.</div>
      </div>
      <div class="adv__progress">
        <div class="adv__progress-bar"><div class="adv__progress-fill" data-adv-bar style="width:0%"></div></div>
        <div class="adv__progress-time" data-adv-time>00:00 remaining</div>
      </div>
      <div class="adv__mode-pick">
        <div class="adv__mode-label">Mode:</div>
        ${['DISCOVERY','DEFENSE'].map((m) => `
          <button data-adv-action="set-mode" data-mode="${m}" class="adv__mode-btn ${mode === m ? 'on' : ''}">
            ${m === 'DISCOVERY' ? '🔎 DISCOVERY' : '🛡 DEFENSE'}
          </button>
        `).join('')}
        <div class="adv__mode-hint dim">Press <kbd>1</kbd> for defense, <kbd>2</kbd> for discovery.</div>
      </div>
      <div class="adv__section">
        <div class="adv__section-title">DISCOVERIES (${a.discoveries.length})</div>
        ${discoveriesHtml}
      </div>
      <div class="adv__section">
        <div class="adv__section-title">SCRIBE</div>
        ${scribeHtml}
      </div>
      <div class="adv__actions">
        <button data-adv-action="recall" class="adv__recall">RECALL CAT (70% payout)</button>
        <button data-adv-action="abandon" class="adv__abandon" title="Sprint home now — forfeit this trip's haul and pay a fine">
          ABANDON &amp; RUSH HOME (forfeit + ${formatCoins(def.abandonFine ?? 0)} fine)
        </button>
      </div>
    </div>
  `
  updateCountdown(view)
}

function updateCountdown(view) {
  const a = currentAdventure()
  const def = currentDef()
  if (!a || !def) return
  const bar = view.querySelector('[data-adv-bar]')
  const timeEl = view.querySelector('[data-adv-time]')
  if (!bar || !timeEl) return
  const now = Date.now()
  const elapsed = Math.max(0, (now - a.startedAt) / 1000)
  const remaining = Math.max(0, (a.endsAt - now) / 1000)
  const pct = Math.min(100, (elapsed / def.duration) * 100)
  bar.style.width = `${pct}%`
  timeEl.textContent = `${formatTime(remaining)} remaining`
}

function formatTime(sec) {
  const s = Math.max(0, Math.floor(sec))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

// First-time discoverability hint on the ADVENTURE rail button. Adds a
// pulsing glow + gold dot until the player opens the panel; then clears it
// and remembers (localStorage) so it never shows again.
function installHint(toggleBtn) {
  if (!toggleBtn) return
  let seen = false
  try { seen = localStorage.getItem(HINT_SEEN_KEY) === '1' } catch { /* private mode */ }
  if (seen) return
  toggleBtn.classList.add('rail-hint')
  toggleBtn.title = 'NEW — travel to Capital City or one of the new lands'
  const clear = () => {
    toggleBtn.classList.remove('rail-hint')
    try { localStorage.setItem(HINT_SEEN_KEY, '1') } catch { /* ignore */ }
    toggleBtn.removeEventListener('click', clear)
  }
  toggleBtn.addEventListener('click', clear)
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}
